import React, { useState, useRef, useEffect } from 'react';
import Grid from '@mui/material/Grid2';
import { PlusCircle, ArrowLeft, User, ChevronLeft } from 'react-feather';
import {
    Box,
    Button,
    TextField,
    Typography,
    Autocomplete,
    Switch,
    Avatar,
    FormControlLabel,
    FormControl,
    Radio,
    RadioGroup,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { authApi, formauthApi } from '../../api/api';
import Toastmessage from '../../components/Toast';
import { logout } from '../../features/auth/AuthSlice';
import { useDispatch } from 'react-redux'
import { useParams } from 'react-router-dom';
import { set } from 'date-fns';

export default function AddClient() {
    const { id } = useParams();
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone_number: '',
        alt_phone_number: '',
        address: '',
        city: '',
        gender: '',
        state: { id: '', state_name: '', shortCode: '' },
        country_code: { id: 1, name: 'USA' },
        zip_code: '',
        password: '',
        profile_image: '',
        confirmPassword: '',
        status: true,
    });
    const [active, setActive] = useState(true);
    const [profile_image, setImageURL] = useState(null);
    const [country_code, setCountry] = useState({ id: '', name: '' })
    const [errors, setErrors] = useState({});
    const [stateOptions, setStateOptions] = useState([]);
    const [state_id, setState] = useState(null);

    const[isLoading , setIsloading] = useState(false);

    const token = useSelector((state_id) => state_id.auth.token); // Get the token from Redux
    const userID = useSelector((state_id) => state_id.auth.id); // Get the userID from Redux

    const dispatch = useDispatch()
    const navigate = useNavigate();

    const inputRefs = useRef({});


    // Set the default country_code on component mount
    useEffect(() => {
        const defaultCountry = countryOptions.find((option) => option.name === 'USA');
        setCountry(defaultCountry);
        listStates();

        const errorFields = Object.keys(errors);
        if (errorFields.length > 0) {
            const firstErrorField = errorFields[0];
            inputRefs.current[firstErrorField]?.focus();
        }
        if (userID) {
            setFormData((prevData) => ({
                ...prevData,
                login_user_id: userID, // Dynamically set userID to formData
            }));
        }

    }, [errors, userID]);

    const listStates = async () => {
        const input_token = 'STATES'; // Replace with your actual token
        const apiUrl = 'list_of_states'; // Replace with the actual API endpoint

        const dataInput = {
        };

        try {
            const data = await authApi(input_token, dataInput, apiUrl, token);

            if (data && data.success) {
                const statesArray = data.list_of_states.map(state_id => ({
                    id: state_id.id,
                    state_name: state_id.state_name,
                    shortCode: state_id.state_short_code
                }));

                setStateOptions(statesArray);
            } else if (data && data.message === 'Token has expired') {
                dispatch(logout());
                localStorage.removeItem('token');
                navigate('/login');
            } else if (data === undefined) {
                await Toastmessage('No response from server', false);
            } else {
                await Toastmessage(data?.message || 'An error occurred', false);
            }

        } catch (error) {
            console.error("Error fetching data:", error);
            if (error.response && error.response.status === 401) {
                dispatch(logout());
                localStorage.removeItem('token');
                await Toastmessage('Session expired. Redirecting to login...', false);
                navigate('/login');
            } else {
                await Toastmessage('Something went wrong', false);
            }
        }
    }

    const countryOptions = [
        { id: 1, name: 'USA' }
    ];

    const handleChange = (field) => (event, value) => {
        if (value && value.id) {
            setState(value); // Pass only the ID to the state_id
        }
        setFormData({ ...formData, [field]: value ?? event.target.value });
    };
    const handleActiveChange = (event) => {
        setActive(event.target.checked);
        setFormData({ ...formData, status: event.target.checked });
    };

    const handleFileUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onloadend = () => {
                setImageURL(reader.result);
                setFormData({ ...formData, profile_image: file }); // Set the file object in formData
            };
            reader.readAsDataURL(file);
        }
    };
    const handleButtonClick = () => {
        fileInputRef.current.click();
    };


    const validateForm = () => {
        const newErrors = {};
        const phoneNumberPattern = /^(?:\+1\s?)?(\d{10})$/; // Matches optional +1 or 1 followed by 10 digits
        // Client Name validation: Required and must be a string (letters and spaces only)
        if (!formData.full_name || formData.full_name.trim() === '') {
            newErrors.full_name = "Client name is required";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.full_name)) {
            newErrors.full_name = "Client name must contain only letters and spaces";
        } else if (formData.full_name.length < 4) {
            newErrors.full_name = "Client Name must be at least 4 characters long";
        }

        // Email validation: Required and valid email format
        if (!formData.email || formData.email.trim() === '') {
            newErrors.email = "Email is required";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            newErrors.email = "Enter a valid email address";
        }

        // Contact Number validation: Required and must be numeric
        if (!formData.phone_number || formData.phone_number.trim() === '') {
            newErrors.phone_number = "Contact number is required";
        } else if (!phoneNumberPattern.test(formData.phone_number.replace(/\s+/g, ''))) {
            newErrors.phone_number = "Contact number must be a valid 10-digit number (e.g., +1 123 4567890)";
        }

        // Alternative Contact Number validation: Optional but must be numeric and follow the same pattern if provided
        if (formData.alt_phone_number && !phoneNumberPattern.test(formData.alt_phone_number.replace(/\s+/g, ''))) {
            newErrors.alt_phone_number = "Alternative contact number must be a valid 10-digit number (e.g., +1 123 4567890)";
        }

        // Address validation
        if (!formData.address || formData.address.trim() === '') {
            newErrors.address = "Address is required";
        }

        // State validation
        if (!formData.state) {
            newErrors.state_id = "State is required";
        }

        // City validation
        if (!formData.city || formData.city.trim() === '') {
            newErrors.city = "City is required";
        } else if (!/^[a-zA-Z\s]+$/.test(formData.city)) {
            newErrors.city = "City name must contain only letters and spaces";
        }

        // Zip Code validation (USA format)
        if (!formData.zip_code || formData.zip_code.trim() === '') {
            newErrors.zip_code = "Zip code is required";
        } else if (!/^\d{5}(-\d{4})?$/.test(formData.zip_code)) {
            newErrors.zip_code = "Invalid zip code format. It must be 5 digits or 5 digits followed by a hyphen and 4 digits (e.g., 12345 or 12345-6789).";
        }


        // Password validation
        // if (!formData.password || formData.password.trim() === '') {
        //     newErrors.password = "Password is required";
        // } else if (formData.password.length < 6) {
        //     newErrors.password = "Password must be at least 6 characters";
        // }

        // // Confirm Password validation
        // if (!formData.confirmPassword || formData.confirmPassword.trim() === '') {
        //     newErrors.confirmPassword = "Confirm password is required";
        // } else if (formData.password !== formData.confirmPassword) {
        //     newErrors.confirmPassword = "Passwords do not match";
        // }

        // Image validation (optional field, but if provided, validate)
        if (!formData.profile_image) {
            const image = formData.profile_image;
            const validImageTypes = ['image/jpeg', 'image/png'];
            const maxSize = 2 * 1024 * 1024; // 2MB in bytes

            if (!validImageTypes.includes(image.type)) {
                newErrors.profile_image = "Please upload a valid image (JPG, JPEG, PNG).";
            } else if (image.size > maxSize) {
                newErrors.profile_image = "Image size must be less than 2MB.";
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };


    const handleSubmit = (event) => {
        event.preventDefault(); // Prevent default form submission behavior

        if (!validateForm()) {
            return; // Exit if validation fails
        }

        const addClientApi = async () => {
            setIsloading(true);
            const input_token = formData.email + 'CREATE_CLIENT';
            const apiUrl = 'create_client';
            // Create a FormData object
            const formDataToSend = new FormData();

            // Append form fields
            formDataToSend.append('login_user_id', userID);
            formDataToSend.append('full_name', formData.full_name);
            formDataToSend.append('email', formData.email);
            formDataToSend.append('phone_number', formData.phone_number);
            formDataToSend.append('alt_phone_number', formData.alt_phone_number || '');
            formDataToSend.append('address', formData.address);
            formDataToSend.append('gender', formData.gender);
            formDataToSend.append('country_code', formData.country_code?.name);
            formDataToSend.append('state_id', formData.state?.id);
            formDataToSend.append('city', formData.city);
            formDataToSend.append('zip_code', formData.zip_code);
            formDataToSend.append('password', formData.password);
            formDataToSend.append('confirmPassword', formData.confirmPassword);
            formDataToSend.append('status', active ? 1 : 0);

            if (formData.profile_image) {
                formDataToSend.append('profile_image', formData.profile_image);
            }
            try {
                const data = await formauthApi(input_token, formDataToSend, apiUrl, token, true);

                if (data && data.success) {
                    await Toastmessage(data.message, true);
                    setTimeout(() => navigate(-1), 2000);
                } else if (data && data.message === 'Token has expired') {
                    setIsloading(false);
                    dispatch(logout());
                    localStorage.removeItem('token');
                    await Toastmessage('Session expired. Redirecting to login...', false);
                    navigate('/login');
                } else if (data === undefined) {
                    setIsloading(false);
                    await Toastmessage('No response from server', false);
                } else {
                    await Toastmessage(data?.message || 'An error occurred', false);
                }
            } catch (error) {
                setIsloading(false);
                console.error("Error fetching data:", error);
                if (error.response && error.response.status === 401) {
                    dispatch(logout());
                    localStorage.removeItem('token');
                    await Toastmessage('Session expired. Redirecting to login...', false);
                    navigate('/login');
                } else {
                    await Toastmessage('Something went wrong', false);
                }
            }
        };

        addClientApi();
    };

    const RequiredLabel = ({ children }) => (
        <>
            {children} <span style={{ color: 'red' }}>*</span>
        </>
    );

    return (


        <Box>

            <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                marginBottom='20px'
                backgroundColor='var(--theme-bg-light)'
                padding={2}
                borderRadius={1}
            >
                {/* Left Section */}
                <Box display="flex" alignItems="center">
                    <User style={{ marginRight: 8, fontSize: '12px', color: 'var(--icon-color)' }} />
                    <Typography fontSize={12} color="var(--text-color)">
                        Add New Client
                    </Typography>
                </Box>

                {/* Right Section */}
                <Button
                    variant="outlined"
                    component={Link}
                    to="/client"
                    sx={{
                        backgroundColor: 'var(--back-button)',
                        color: "var(--back-color)",
                        fontWeight: 'bold',
                        border: 'none',
                        padding: '10px 20px'
                    }}
                    startIcon={<ChevronLeft sx={{ fontSize: 17 }} />}
                    style={{ textTransform: 'none' }}
                >
                    Back Client list
                </Button>
            </Box>



            <Box className="card-box" sx={{ p: { xs: 2, sm: 2, md: 3 }, m: { xs: 1, sm: 2, md: 3 }, border: '1px solid var(--border-color)', borderRadius: '8px' }}>
                {/* Header Section */}
                <Box className="sub-header">
                    <Typography sx={{ color: '#3570FF', fontSize: '14px', fontWeight: 'bold' }}>
                        {/* <Link to="/client" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
                                <ArrowLeft size={20} style={{ marginRight: '8px' }} />  */}
                        Add New Client
                        {/* </Link> */}
                    </Typography>
                    <FormControlLabel
                        control={<Switch checked={active} onChange={handleActiveChange} />}
                        label={
                            <Typography sx={{ color: active ? 'green' : 'red', fontWeight: 'bold' }}>
                                Active : {active ? 'Yes' : 'No'}
                            </Typography>
                        }
                    />
                </Box>
                {/* Form Fields */}
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography><RequiredLabel>Client Name</RequiredLabel></Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter the client Name"
                            value={formData.full_name}
                            error={!!errors.full_name}
                            helperText={errors.full_name} // Display error message
                            onChange={handleChange('full_name')}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            inputRef={(el) => (inputRefs.current.full_name = el)}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography><RequiredLabel>Client Email ID</RequiredLabel></Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter the Email ID"
                            value={formData.email}
                            error={!!errors.email}
                            helperText={errors.email} // Display error message
                            onChange={handleChange('email')}
                            inputRef={(el) => (inputRefs.current.email = el)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography><RequiredLabel>Client Contact Number</RequiredLabel></Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter Contact Number"
                            value={formData.phone_number}
                            error={!!errors.phone_number}
                            helperText={errors.phone_number} // Display error message
                            onChange={handleChange('phone_number')}
                            inputRef={(el) => (inputRefs.current.phone_number = el)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography>Client Alternative Contact Number</Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter Alternative Contact Number"
                            value={formData.alt_phone_number}
                            error={!!errors.alt_phone_number} // This marks the field as errored
                            helperText={errors.alt_phone_number} // This displays the error message
                            onChange={handleChange('alt_phone_number')}
                            inputRef={(el) => (inputRefs.current.alt_phone_number = el)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Typography><RequiredLabel>Client Address</RequiredLabel></Typography>
                        <TextField
                            fullWidth
                            multiline
                            minRows={3}
                            variant="outlined"
                            placeholder="Enter the Address"
                            value={formData.address}
                            error={!!errors.address}
                            helperText={errors.address} // Display error message
                            onChange={handleChange('address')}
                            inputRef={(el) => (inputRefs.current.address = el)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography sx={{ mb: 1, fontWeight: '500', color: '#595A63' }}>Gender</Typography>
                        <FormControl component="fieldset" error={!!errors.gender}>
                            <RadioGroup
                                row
                                aria-label="gender"
                                name="gender"
                                value={formData.gender}
                                // onChange={(event, newValue) => setGender(newValue)}
                                onChange={handleChange('gender')}
                            >
                                <FormControlLabel value="female" control={<Radio />} label="Female" />
                                <FormControlLabel value="male" control={<Radio />} label="Male" />
                                <FormControlLabel value="other" control={<Radio />} label="Other" />
                            </RadioGroup>
                            {errors.gender && (
                                <Typography variant="caption" color="error">
                                    {errors.gender}
                                </Typography>
                            )}
                        </FormControl>
                    </Grid>
                    <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                        <Typography>Client Photo</Typography>
                        <Box display="flex" flexDirection="row" alignItems="center" gap={3}>
                            <Avatar
                                variant="rounded"
                                src={profile_image || ""}
                                sx={{ width: 100, height: 100, mb: 1 }}
                            />
                            <Box display="flex" flexDirection="column" alignItems="flex-start" justifyContent="center">
                                <input
                                    type="file"
                                    accept="image/jpeg, image/png"
                                    style={{ display: 'none' }}
                                    ref={fileInputRef}
                                    onChange={handleFileUpload}
                                />
                                <Button sx={{ backgroundColor: 'var(--back-button)',color: "var(--back-color)", fontWeight: "600", padding: "10px 20px" }} onClick={handleButtonClick}>
                                    Upload Image
                                </Button>
                                <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
                                    JPG or PNG no larger than 5 MB
                                </Typography>
                                {/* Display image error */}
                                {errors.profile_image && (
                                    <Typography variant="caption" color="error" sx={{ mt: 1 }}>
                                        {errors.profile_image}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography><RequiredLabel>Country</RequiredLabel></Typography>
                        <Autocomplete
                            options={countryOptions}
                            getOptionLabel={(option) => option.name}
                            value={country_code}
                            fullWidth
                            disabled
                            onChange={handleChange('country_code')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select Country"
                                    variant="outlined"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography><RequiredLabel>State</RequiredLabel></Typography>
                        <Autocomplete
                            options={stateOptions}
                            getOptionLabel={(option) => option.state_name}
                            value={formData.state} // Match by ID
                            fullWidth
                            onChange={handleChange('state')}
                            inputRef={(el) => (inputRefs.current.state_id = el)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Select state"
                                    variant="outlined"
                                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                                    error={!!errors.state_id} // Show error if there is one
                                    helperText={errors.state_id} // Display error message
                                />
                            )}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography><RequiredLabel>City</RequiredLabel></Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter the city name"
                            value={formData.city}
                            error={!!errors.city}
                            helperText={errors.city} // Display error message
                            onChange={handleChange('city')}
                            inputRef={(el) => (inputRefs.current.city = el)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Grid>

                    <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                        <Typography><RequiredLabel>Zip Code</RequiredLabel></Typography>
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Enter the zip Code"
                            value={formData.zip_code}
                            error={!!errors.zip_code}
                            helperText={errors.zip_code} // Display error message
                            onChange={handleChange('zip_code')}
                            inputRef={(el) => (inputRefs.current.zip_code = el)}
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                    </Grid>

                </Grid>
                <Box mt={4} textAlign="center">
                    <Button
                        type="submit"
                        variant="contained"
                        startIcon={<PlusCircle size={20} />}
                        onClick={handleSubmit}
                        disabled={isLoading}
                         className='btn-theme-button'
                    >
                        Add New Client
                    </Button>
                </Box>
            </Box>
            {/* <Box
                    sx={{
                        padding: '24px',
                        marginTop: "20px",
                        backgroundColor: '#FFF',
                        borderRadius: '8px',
                        boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                    }}
                >
                    <Typography variant="h6">Account Creation</Typography>
                    <Grid container spacing={3}>
                        <Grid size={4}>
                            <Typography><RequiredLabel>Password</RequiredLabel></Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="password"
                                placeholder="Enter Password"
                                value={formData.password}
                                error={!!errors.password}
                                helperText={errors.password} // Display error message
                                onChange={handleChange('password')}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            />
                        </Grid>
                        <Grid size={4}>
                            <Typography><RequiredLabel>Confirm Password</RequiredLabel></Typography>
                            <TextField
                                fullWidth
                                variant="outlined"
                                type="password"
                                placeholder="Confirm Password"
                                value={formData.confirmPassword}
                                error={!!errors.confirmPassword}
                                helperText={errors.confirmPassword} // Display error message
                                onChange={handleChange('confirmPassword')}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                            />
                        </Grid>
                    </Grid>
                    
                </Box> */}
        </Box>

    );
}
